import { Badge } from '@tempo-io/sui-badge';
import { cn } from '@tempo-io/sui-class-utils';
import { ExpandLess } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useState } from 'react';
import type { LabelWithField } from '@/common/components/ui/swimlane/types';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import { SwimlaneCustomerChip } from '@/features/items/components/custom-field-value/CustomerFieldDisplay';

export const DEFAULT_LABELS_TO_SHOW_STANDARD = 3;
export const DEFAULT_LABELS_TO_SHOW_COMPACT = 2;

function getLabelColorStyle(color: string): React.CSSProperties | undefined {
  if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
    return { borderColor: `${color}99`, backgroundColor: `${color}14` };
  }
  if (color.startsWith('rgb')) {
    return {
      borderColor: color.replace('rgb(', 'rgba(').replace(')', ', 0.6)'),
      backgroundColor: color.replace('rgb(', 'rgba(').replace(')', ', 0.08)'),
    };
  }
  return undefined;
}

export function Labels({
  labels = [],
  isCompact,
  color,
}: {
  labels?: LabelWithField[];
  isCompact?: boolean;
  color?: string;
}) {
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');
  const [collapsed, setCollapsed] = useState(true);
  const sanitizedLabels = labels.filter((label) => Boolean(label?.value));

  const defaultLabelsToShow = isCompact
    ? DEFAULT_LABELS_TO_SHOW_COMPACT
    : DEFAULT_LABELS_TO_SHOW_STANDARD;

  if (sanitizedLabels.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('flex w-full gap-1.5', {
        'flex-wrap': !collapsed,
      })}
      data-testid="label-list"
    >
      {sanitizedLabels
        .slice(0, collapsed ? defaultLabelsToShow : sanitizedLabels.length)
        .map((label) => {
          const customerDisplayValues = ideaManagerEnabled
            ? (label.displayValues?.filter((displayValue) => displayValue.type === 'customer') ??
              [])
            : [];

          return (
            <Tooltip key={`${label.fieldName}:${label.value}`}>
              <TooltipTrigger asChild>
                <Badge
                  className={cn(
                    'rounded-md border px-2 py-0.5 text-[10px] text-ellipsis block',
                    collapsed && 'flex-1 min-w-px max-w-fit hover:w-full',
                  )}
                  style={color ? getLabelColorStyle(color) : undefined}
                  data-testid="list-item-label"
                >
                  {customerDisplayValues.length > 0
                    ? customerDisplayValues.map((customer, index) => (
                        <SwimlaneCustomerChip
                          key={customer.id}
                          customer={customer}
                          compact
                          className={cn(index > 0 && 'ml-2')}
                        />
                      ))
                    : label.value}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{label.fieldName}</TooltipContent>
            </Tooltip>
          );
        })}
      {sanitizedLabels.length > defaultLabelsToShow && (
        <Badge asChild>
          <button type="button" className="cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? (
              `+${sanitizedLabels.length - defaultLabelsToShow}`
            ) : (
              <ExpandLess size={16} />
            )}
          </button>
        </Badge>
      )}
    </div>
  );
}
